<template>
  <div>
    <!-- Comment content -->
    <div v-if="comment && !getReported && !reported" class="comment-wrapper">
      <div class="d-flex">
        <editable-avatar
          :image="comment.poster_image"
          :editable="false"
          :size="$vuetify.breakpoint.xs ? 30 : 40"
          class="mr-3"
        />

        <div class="d-flex flex-column pa-3 comment-box">
          <!-- Poster Info -->
          <div class="d-flex justify-space-between pb-2">
            <span
              v-text="safePosterName"
              class="url-button"
              @click="goToProfile(comment.poster, comment.poster_type)"
              :class="$vuetify.breakpoint.xs ? 'font-12' : 'font-14'"
            />

            <div class="d-flex align-center">
              <span style="font-size: 10px; color: darkgray" v-text="getTimeAgo(comment.createdAt)" />
              
              <v-menu offset-y rounded="lg" left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon class="ml-1" v-bind="attrs" v-on="on" small>
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(item, index) in getDropMenu(comment)"
                    :key="index"
                    link
                    @click="onSelectedMenu(item)"
                  >
                    <v-list-item-title class="mr-3">
                      <v-icon v-text="item.icon" class="mr-3" />
                      <span v-text="item.title" />
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <!-- Comment Text -->
          <span
            v-if="!editingComment"
            v-text="safeCommentText"
            :class="($vuetify.breakpoint.xs ? 'font-12 ' : 'font-14 ') + (comment.file ? 'mb-3' : 'mb-0')"
            class="text-block"
          />

          <!-- Edit Mode -->
          <div v-else>
            <div class="d-flex align-center">
              <v-textarea
                v-model="editedText"
                placeholder="Add a comment..."
                rows="1"
                auto-grow
                dense
                flat
                solo
                hide-details
                background-color="#f2f2f2"
                ref="comment"
                class="flex-grow-1 mr-2"
              />
              <v-menu
                bottom
                offset-y
                origin="bottom bottom"
                :close-on-content-click="false"
                v-model="emojiKeyboard"
              >
                <template v-slot:activator="{ on: menu, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="{ ...menu }"
                    v-if="$vuetify.breakpoint.xs"
                  >
                    <v-icon>mdi-emoticon-happy-outline</v-icon>
                  </v-btn>
                  <v-tooltip top v-else>
                    <template v-slot:activator="{ on: tooltip }">
                      <v-btn icon v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                        <v-icon>mdi-emoticon-happy-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Open Emoji Keyboard</span>
                  </v-tooltip>
                </template>
                <v-card>
                  <VEmojiPicker @select="selectEmoji" />
                </v-card>
              </v-menu>
            </div>

            <div class="mt-2 mb-2">
              <v-btn
                rounded
                color="primary"
                class="mr-2 mb-2"
                :disabled="safeCommentText === editedText || !editedText"
                @click="onUpdateComment"
                :loading="isUpdatingComment"
                :x-small="$vuetify.breakpoint.xs"
              >Save changes</v-btn>
              <v-btn
                rounded
                @click="onCancelUpdateComment"
                :x-small="$vuetify.breakpoint.xs"
                class="mb-2"
              >Cancel</v-btn>
            </div>
          </div>

          <!-- Comment File -->
          <div v-if="comment.file">
            <img
              :src="comment.file"
              contain
              style="max-height: 240px; object-fit: contain; max-width: 100%"
            />
          </div>
        </div>
      </div>

      <!-- Like and Reaction -->
      <div class="d-flex align-center ml-10 mt-2">
        <comment-like-board
          :onLike="onLike"
          :liked="likeData ? likeData.liked : null"
        />
        <comment-reaction-details
          :data="likeData"
          :_id="comment._id"
          v-if="likeData"
        />
      </div>

      <!-- Delete & Report Dialogs -->
      <confirm-delete-dialog
        :deleteDialog="deleteDialog"
        :onClose="onCloseConfirmDelete"
        :onConfirm="onConfirmDelete"
        text="This action cannot be undone. Are you sure you want to delete the comment?"
        :loading="loading"
      />
      <report-event-dialog
        type="Comment"
        :_id="comment._id"
        :dialog="reportDialog"
        :onClose="onCloseReportDialog"
        :onReported="onReported"
        v-if="reportDialog && comment && comment._id"
      />
    </div>

    <div v-if="reported">Comment has been reported.</div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import DOMPurify from "dompurify";
import CommentLikeBoard from "./CommentLikeBoard.vue";
import EditableAvatar from "@/components/EditableAvatar.vue";
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import CommentReactionDetails from "./CommentReactionDetails.vue";
import ReportEventDialog from "./ReportEventDialog.vue";

export default {
  components: {
    CommentLikeBoard,
    EditableAvatar,
    ConfirmDeleteDialog,
    CommentReactionDetails,
    ReportEventDialog
  },
  props: {
    comment: Object,
    onUpdatedComment: Function,
    onDeletedComment: Function
  },
  data() {
    return {
      likeData: null,
      editingComment: false,
      editedText: "",
      isUpdatingComment: false,
      deleteDialog: false,
      reportDialog: false,
      loading: false,
      reported: false,
      emojiKeyboard: false
    };
  },
  computed: {
    ...mapState("auth", { profile: "profile", profileType: "type" }),
    
    getReported() {
      if (!this.comment?.reported?.length) return null;
      const reportedByMe = this.comment.reported.find(r => r._id === this.profile._id);
      return reportedByMe?.reason || null;
    },

    safeCommentText() {
      return DOMPurify.sanitize(this.comment?.text || "", { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    },

    safePosterName() {
      const name = this.comment?.poster_name || `${this.comment?.first_name || ''} ${this.comment?.last_name || ''}`;
      return name.length > 50 ? name.slice(0, 50) + "..." : name;
    }
  },
  methods: {
    ...mapActions("event", { deleteComment: "deleteComment", editComment: "editComment", getCommentLikeCount: "getCommentLikeCount", like: "commentLike" }),

    getDropMenu(comment) {
      return comment.poster === this.profile._id
        ? [{ title: "Edit", icon: "mdi-pencil" }, { title: "Delete", icon: "mdi-delete" }]
        : [{ title: "Report", icon: "mdi-flag" }];
    },

    getLiked() {
      this.loading = true;
      this.getCommentLikeCount({ _id: this.comment._id })
        .then(data => { this.loading = false; this.likeData = data; })
        .catch(() => this.loading = false);
    },

    onLike(type) {
      this.like({ _id: this.comment._id, type })
        .then(data => { this.likeData = { ...this.likeData, ...data }; })
        .catch(err => console.error(err));
    },

    getTimeAgo(oldTimeStr) {
      const oldTime = Date.parse(oldTimeStr);
      const diff = (new Date().getTime() - oldTime) / 1000;
      const months = Math.floor(diff / (3600 * 24 * 30));
      const days = Math.floor(diff / (3600 * 24)) % 30;
      const hours = Math.floor(diff / 3600) % 24;
      const mins = Math.floor(diff / 60) % 60;
      if (months) return months === 1 ? "1 month" : `${months} months`;
      if (days) return days === 1 ? "1 day" : `${days} days`;
      if (hours) return hours === 1 ? "1 hour" : `${hours} hours`;
      if (mins) return mins === 1 ? "1 min" : `${mins} min`;
      return "less than 1 min";
    },

    onSelectedMenu(item) {
      if (item.title === "Delete") this.deleteDialog = true;
      else if (item.title === "Edit") { this.editedText = this.safeCommentText; this.editingComment = true; }
      else if (item.title === "Report") this.reportDialog = true;
    },

    onCancelUpdateComment() { this.editingComment = false; },

    selectEmoji(emoji) { this.insertText(emoji.data); this.makeFocus(); },

    insertText(text) {
      const el = this.$refs.comment.$refs.input;
      const cursorPos = el.selectionEnd;
      this.editedText = this.editedText.slice(0, cursorPos) + text + this.editedText.slice(cursorPos);
      this.$nextTick(() => el.setSelectionRange(cursorPos + text.length, cursorPos + text.length));
    },

    makeFocus() { this.$refs.comment.$refs.input.focus(); },

onUpdateComment() {
  // Force everything into plain text only (no tags)
const sanitizedText = DOMPurify.sanitize(this.editedText, {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a'],
  ALLOWED_ATTR: ['href', 'title', 'target']
});


  // If DOMPurify strips everything, keep original as plain text
  const finalText = sanitizedText || this.editedText.replace(/</g, "&lt;").replace(/>/g, "&gt;");

  this.isUpdatingComment = true;
  this.editComment({ text: finalText, _id: this.comment._id })
    .then(data => {
      this.isUpdatingComment = false;
      this.editingComment = false;
      this.onUpdatedComment && this.onUpdatedComment(data);
    })
    .catch(() => (this.isUpdatingComment = false));
},

    onConfirmDelete() { this.loading = true; this.deleteComment({ _id: this.comment._id })
      .then(data => { this.loading = false; this.deleteDialog = false; this.onDeletedComment && this.onDeletedComment(data); })
      .catch(() => (this.loading = false)); 
    },

    onCloseConfirmDelete() { this.deleteDialog = false; },

    onCloseReportDialog() { this.reportDialog = false; },

    onReported() { this.reportDialog = false; this.reported = true; },

    goToProfile(poster, type) {
      const routes = { Agency: "agency-profile-details", Volunteer: "volunteer-profile-details" };
      if (routes[type]) this.$router.push({ name: routes[type], params: { id: poster } });
    }
  },

  mounted() { this.getLiked(); }
};
</script>

<style scoped>
.comment-wrapper { margin-bottom: 1rem; }
.comment-box { background-color: #f2f2f2; color: black; width: 100%; border-radius: 0 10px 10px 10px; }
.text-block { word-break: break-word; }
</style>

<style scoped>
.comment-wrapper {
  margin-bottom: 1rem;
}
.comment-box {
  background-color: #f2f2f2;
  color: black;
  width: 100%;
  border-radius: 0 10px 10px 10px;
}
.text-block {
  word-break: break-word;
}
</style>
